import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { strings } from '../strings';
import { ALLOWED_MIME_TYPES, validateFile } from '../attachments/mime';

export interface AttachmentsProps {
  /** Selected files, owned by the parent (`Modal`) — this component never uploads them. */
  files: File[];
  onChange: (files: File[]) => void;
  /** Disables adding/removing files — set by `Modal` while a submit is in flight. */
  disabled?: boolean;
}

interface SelectionError {
  fileName: string;
  reason: string;
}

const INPUT_ID = 'ft-attachments-input';
const ACCEPT = ALLOWED_MIME_TYPES.join(',');

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/** Short uppercase chip text for a non-image file, e.g. "application/pdf" -> "PDF". */
function chipLabel(contentType: string): string {
  const subtype = contentType.split('/')[1] ?? 'file';
  const short = subtype === 'plain' ? 'txt' : subtype;
  return short.slice(0, 4).toUpperCase();
}

/**
 * Multi-file picker for the modal's Attachments section. Purely a *selection* UI — files are
 * held as plain `File` objects and reported to the parent via `onChange`; nothing here ever
 * uploads (`uploadFile` in `../attachments/upload` does that, and only gets called at submit
 * time, per Task 9).
 *
 * Object URLs (for image thumbnails) are created/revoked synchronously as part of rendering,
 * keyed by file identity in a `Map` held in a ref. That's deliberate: re-scanning a handful of
 * files on every render is cheap, and it means a thumbnail's URL exists in the very same render
 * that introduces its file — no flash-of-no-thumbnail from deferring the work to an effect. The
 * `useEffect` below only covers the unmount case, revoking whatever's left in the map so closing
 * the modal never leaks a `blob:` URL for the rest of the page's lifetime.
 */
export function Attachments({ files, onChange, disabled }: AttachmentsProps) {
  const [errors, setErrors] = useState<SelectionError[]>([]);
  const objectUrlsRef = useRef(new Map<File, string>());

  const urlMap = objectUrlsRef.current;
  for (const file of Array.from(urlMap.keys())) {
    if (!files.includes(file)) {
      URL.revokeObjectURL(urlMap.get(file)!);
      urlMap.delete(file);
    }
  }
  for (const file of files) {
    if (isImageFile(file) && !urlMap.has(file)) {
      urlMap.set(file, URL.createObjectURL(file));
    }
  }

  // Unmount-only cleanup — revokes whatever's still in the map (the ref, so this reads the
  // latest state at cleanup time rather than whatever was captured when the effect was set up).
  useEffect(() => {
    return () => {
      urlMap.forEach((url) => URL.revokeObjectURL(url));
      urlMap.clear();
    };
    // Empty deps: this is an unmount-only cleanup, and `urlMap` is a ref
    // whose identity never changes (see the note above).
  }, []);

  const handleFileChange = (event: JSX.TargetedEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const selected = Array.from(input.files ?? []);
    const accepted: File[] = [];
    const rejected: SelectionError[] = [];

    for (const file of selected) {
      const result = validateFile(file);
      if (result.ok) {
        accepted.push(file);
      } else {
        rejected.push({ fileName: file.name, reason: result.reason });
      }
    }

    if (accepted.length > 0) onChange([...files, ...accepted]);
    setErrors(rejected);
    // Reset so re-selecting the exact same file (e.g. after removing it) still fires `change`.
    input.value = '';
  };

  const handleRemove = (index: number) => {
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div class="ft-attachments">
      <span class="ft-attachments-label">{strings.attachments.heading}</span>

      <label class="ft-attachments-add" for={INPUT_ID}>
        {strings.attachments.addLabel}
      </label>
      <input
        id={INPUT_ID}
        class="ft-attachments-input"
        type="file"
        multiple
        accept={ACCEPT}
        onChange={handleFileChange}
        disabled={disabled}
      />

      {errors.length > 0 ? (
        <ul class="ft-attachment-errors">
          {errors.map((error, index) => (
            <li key={`${error.fileName}-${index}`} class="ft-hint" role="alert">
              {error.fileName}: {error.reason}
            </li>
          ))}
        </ul>
      ) : null}

      {files.length > 0 ? (
        <ul class="ft-attachments-list">
          {files.map((file, index) => {
            const url = urlMap.get(file);
            return (
              <li
                key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                class="ft-attachment-item"
              >
                {url ? (
                  <img src={url} alt="" class="ft-attachment-thumb" />
                ) : (
                  <span class="ft-attachment-chip" aria-hidden="true">
                    {chipLabel(file.type)}
                  </span>
                )}
                <span class="ft-attachment-name">{file.name}</span>
                <button
                  type="button"
                  class="ft-attachment-remove"
                  aria-label={strings.attachments.removeLabel(file.name)}
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                >
                  ×
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
